import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import XYZ from 'ol/source/XYZ'
import { defaults as defaultControls, MousePosition, Rotate, ScaleLine } from 'ol/control'
import type BaseLayer from 'ol/layer/Base'
import { GOOGLE_BASEMAPS_ENABLED } from '../config'
import { createGoogleTileLayer } from './googleTiles'

/**
 * Basemaps load directly from public tile services — they do not transit the
 * proxy and do not appear in usage logs (PRD P0.6).
 */
export interface Basemap {
  id: string
  title: string
  layer: BaseLayer | null
}

export function createBasemaps(): Basemap[] {
  // crossOrigin:'anonymous' keeps the canvas untainted for PNG export (P1.5);
  // both tile services serve CORS-enabled tiles.
  const osm = new TileLayer({ source: new OSM({ crossOrigin: 'anonymous' }), zIndex: 0, visible: true })
  const topo = new TileLayer({
    source: new XYZ({
      url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
      crossOrigin: 'anonymous',
      attributions:
        'Map data © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Style © <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
      maxZoom: 17,
    }),
    zIndex: 0,
    visible: false,
  })
  const basemaps: Basemap[] = [
    { id: 'osm', title: 'OpenStreetMap', layer: osm },
    { id: 'topo', title: 'OpenTopoMap', layer: topo },
    { id: 'none', title: 'No basemap', layer: null },
  ]
  if (GOOGLE_BASEMAPS_ENABLED) {
    basemaps.push(
      { id: 'google-roadmap', title: 'Google Maps', layer: createGoogleTileLayer('roadmap') },
      { id: 'google-satellite', title: 'Google Satellite', layer: createGoogleTileLayer('satellite') },
      { id: 'google-terrain', title: 'Google Relief', layer: createGoogleTileLayer('terrain') },
    )
  }
  return basemaps
}

export function selectBasemap(basemaps: Basemap[], id: string): void {
  for (const bm of basemaps) bm.layer?.setVisible(bm.id === id)
}

class NorthArrowControl extends Rotate {
  constructor(label: HTMLElement) {
    super({
      autoHide: false,
      className: 'north-arrow',
      compassClassName: 'north-arrow__compass',
      label,
      tipLabel: 'North — reset map orientation',
    })
    this.element
      .querySelector('button')
      ?.setAttribute('aria-label', 'North — reset map orientation')
  }
}

function createNorthArrow(): Rotate {
  const compass = document.createElement('span')
  compass.innerHTML =
    '<span class="north-arrow__letter" aria-hidden="true">N</span>' +
    '<span class="north-arrow__needle" aria-hidden="true"></span>'
  return new NorthArrowControl(compass)
}

export function createMap(target: HTMLElement, basemaps: Basemap[]): Map {
  return new Map({
    target,
    layers: basemaps.flatMap((bm) => (bm.layer ? [bm.layer] : [])),
    view: new View({ center: [-10700000, 8400000], zoom: 4 }), // Canada fallback until capabilities fit
    controls: defaultControls({ attributionOptions: { collapsible: false }, rotate: false }).extend([
      createNorthArrow(),
      new ScaleLine({ units: 'metric', bar: true, text: true, minWidth: 100 }),
      // Live cursor coordinates (P1.7) — lat-first, ~1 m precision; the
      // control stays empty on touch devices (no mouse).
      new MousePosition({
        projection: 'EPSG:4326',
        coordinateFormat: (coordinate) => {
          if (!coordinate) return ''
          const [lon, lat] = coordinate
          return `${lat.toFixed(5)}, ${lon.toFixed(5)}`
        },
        placeholder: '',
      }),
    ]),
  })
}
